import * as React from 'react';
import * as t from 'io-ts';
import { useForm } from 'react-hook-form';
import { SemibigHeader } from '@narvar/nth-bit-header';
import SpinnerIcon from '@narvar/nth-bit-icon-spinner';
import Paragraph from '@narvar/nth-bit-paragraph';
import Button, { ButtonType } from '@narvar/nth-block-button';
import { Column } from '@narvar/nth-grid';
import { usePageContext } from '@narvar/nth-hook-context-page';
import {
  useTranslations,
  withTranslationsProvider,
} from '@narvar/nth-hook-translations';
import PanelHeaderText from '../../PanelHeaderText';
import { ActionsRow, FormColumn, FormRow } from '../../form-layout';
import RegisteredInput from '../../form-fields/RegisteredInput';
import TextLink from '../../TextLink';
import {
  UI_STATE,
  useRedirectToHoldContext,
} from '../../../hooks/useRedirectToHoldState';
import ErrorAlert from '../../ErrorAlert';
import { getEligibility } from '../../../api/redirect-to-hold';
import {
  CarrierKindV,
  RequestDeliveryInstructionsAddressVerificationFields,
  RequestRedirectToHoldEligibility,
  ResponseErrorCode,
} from '../../../../server/api/schema';
import { FormData, FormDataV, InputNames } from './schema';
import { decode } from '../../../../server/lib/codec';
import CustomComponentContext from '../../CustomComponentContext';

const EligibilityInputsV = t.strict({
  carrier: CarrierKindV,
  trackingNumber: t.string,
  retailerMoniker: t.string,
  uniqueIdentifier: t.string,
});

interface Props {
  countryCode: string;
}

const VerifyAddressPanel: React.FunctionComponent<Props> = ({
  countryCode,
}: Props) => {
  const { track } = React.useContext(CustomComponentContext);
  const { state: rthState, update: updateRthState } =
    useRedirectToHoldContext();
  const { state: pageContext } = usePageContext();

  const {
    redirectToHoldAddressLine1,
    redirectToHoldAddressLine2,
    redirectToHoldPostalCode,
    redirectToHoldStep1,
    redirectToHoldVerifyAddressHeader,
    redirectToHoldVerifyAddressDescription,
    redirectToHoldCancel,
    redirectToHoldVerify,
    redirectToHoldVerifyError,
    redirectToHoldIneligibleError,
    redirectToHoldRequiredFieldError,
    redirectToHoldSystemError,
  } = useTranslations();

  const [showVerificationError, setShowVerificationError] =
    React.useState(false);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [errorText, setErrorText] = React.useState(redirectToHoldSystemError);

  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
  } = useForm<FormData>();

  const logRedirectToHoldEvent = (opts: { clickSubType: string }) => {
    track({
      eventType: 'click',
      ...opts,
    });
  };

  const onSubmit = handleSubmit(async (rawFormData: unknown) => {
    if (isSubmitting) return;

    setIsSubmitting(true);
    setShowVerificationError(false);

    logRedirectToHoldEvent({
      clickSubType: 'will_verify_address',
    });

    try {
      const formData = decode(FormDataV, rawFormData);

      const { carrier, trackingNumber, retailerMoniker, uniqueIdentifier } =
        decode(EligibilityInputsV, {
          carrier: pageContext.carrier,
          trackingNumber: pageContext.trackingNumbers?.[0],
          retailerMoniker: pageContext.retailerMoniker,
          uniqueIdentifier: rthState.uniqueIdentifier,
        });

      const addressFields: RequestDeliveryInstructionsAddressVerificationFields =
        {
          firstName: '',
          lastName: '',
          addressLine1: formData.addressLine1,
          addressLine2: formData.addressLine2,
          city: '',
          stateCode: '',
          postalCode: formData.postalCode,
          countryCode,
        };

      const request: RequestRedirectToHoldEligibility = {
        uniqueIdentifier,
        retailerMoniker,
        carrierMoniker: carrier,
        addressVerification: addressFields,
      };

      const result = await getEligibility({
        carrier,
        trackingNumber,
        request,
      });

      setIsSubmitting(false);

      if (result.success === false) {
        switch (result.error) {
          case ResponseErrorCode.ADDRESS_VERIFICATION_ERROR:
            setErrorText(redirectToHoldVerifyError);
            setShowVerificationError(true);
            return;
          case ResponseErrorCode.INELIGIBLE_ERROR:
            setErrorText(redirectToHoldIneligibleError);
            setShowVerificationError(true);
            return;
          default:
            throw new Error('Unsuccessful response');
        }
      }

      logRedirectToHoldEvent({
        clickSubType: 'did_verify_address',
      });

      const eligibilityWarning =
        result.success === true && result.warning !== null
          ? result.warning
          : null;
      updateRthState({
        ...rthState,
        uiState: UI_STATE.SELECT_LOCATION,
        address: addressFields,
        eligibilityWarning,
      });
    } catch {
      setIsSubmitting(false);
      setErrorText(redirectToHoldSystemError);
      setShowVerificationError(true);
    }
  });

  const handleCancel: React.MouseEventHandler<HTMLButtonElement> = () => {
    logRedirectToHoldEvent({
      clickSubType: 'abandoned_verify_address',
    });
    updateRthState({
      ...rthState,
      uiState: UI_STATE.INITIAL,
    });
  };

  // Reset the form when the component unmounts. There's no flow
  // to return back to the form with values remaining in the fields,
  // So once we're done with this panel, reset it right away.
  React.useEffect(() => reset, []);

  return (
    <div style={{ paddingTop: '0.5rem' }}>
      <PanelHeaderText>{redirectToHoldStep1}</PanelHeaderText>
      <SemibigHeader as="h2">{redirectToHoldVerifyAddressHeader}</SemibigHeader>
      <Paragraph containerCustomStyle={{ margin: '1rem 0 1.5rem 0' }}>
        {redirectToHoldVerifyAddressDescription}
      </Paragraph>
      <ErrorAlert
        show={showVerificationError}
        focusOnShow
        error
        dismissible={false}
        role="alert"
        timeout={0}
        s={{
          padding: '1rem',
          margin: '0 0 2rem 0',
          borderRadius: '.25rem',
        }}
      >
        <Paragraph containerCustomStyle={{ margin: 0, color: '#000' }}>
          {errorText}
        </Paragraph>
      </ErrorAlert>
      <form
        noValidate
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        onSubmit={onSubmit}
      >
        <FormRow>
          <FormColumn sm-columns={5}>
            <RegisteredInput
              id={InputNames.addressLine1}
              label={redirectToHoldAddressLine1 ?? ''}
              animateLabel
              errorMessage={errors.addressLine1?.message ?? ''}
              value={watch(InputNames.addressLine1)}
              register={register(InputNames.addressLine1, {
                required: {
                  value: true,
                  message: redirectToHoldRequiredFieldError ?? '',
                },
              })}
            />
          </FormColumn>
          <FormColumn sm-columns={5}>
            <RegisteredInput
              id={InputNames.addressLine2}
              label={redirectToHoldAddressLine2 ?? ''}
              animateLabel
              errorMessage={errors.addressLine2?.message ?? ''}
              value={watch(InputNames.addressLine2)}
              register={register(InputNames.addressLine2)}
            />
          </FormColumn>
          <FormColumn sm-columns={2}>
            <RegisteredInput
              id={InputNames.postalCode}
              label={redirectToHoldPostalCode ?? ''}
              animateLabel
              errorMessage={errors.postalCode?.message ?? ''}
              value={watch(InputNames.postalCode)}
              register={register(InputNames.postalCode, {
                required: {
                  value: true,
                  message: redirectToHoldRequiredFieldError ?? '',
                },
              })}
            />
          </FormColumn>
        </FormRow>
        <ActionsRow>
          <FormColumn xs-columns={12} sm-columns={3}>
            <Button big type={ButtonType.submit} disabled={isSubmitting}>
              {isSubmitting ? <SpinnerIcon /> : redirectToHoldVerify}
            </Button>
          </FormColumn>
          <Column sm-columns={3}>
            <TextLink onClick={handleCancel}>{redirectToHoldCancel}</TextLink>
          </Column>
        </ActionsRow>
      </form>
    </div>
  );
};

export default withTranslationsProvider(VerifyAddressPanel, {
  translationKeys: [
    'redirectToHoldAddressLine1',
    'redirectToHoldAddressLine2',
    'redirectToHoldPostalCode',
    'redirectToHoldStep1',
    'redirectToHoldVerifyAddressHeader',
    'redirectToHoldVerifyAddressDescription',
    'redirectToHoldCancel',
    'redirectToHoldVerify',
    'redirectToHoldVerifyError',
    'redirectToHoldIneligibleError',
    'redirectToHoldRequiredFieldError',
    'redirectToHoldSystemError',
  ],
});
